"use client";

import { createContext, useContext, useEffect, useState } from "react";
import {
  User,
  UserCredential,
  signInWithEmailAndPassword,
  ParsedToken,
  signInWithPopup,
  GoogleAuthProvider,
  signOut
} from "firebase/auth";
import { auth, db } from "@/firebase/client";
import { setToken, removeToken } from "@/actions/auth";
import { collection, getDoc, query, setDoc, where, getDocs } from "firebase/firestore";
import { doc } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { useRouter } from "next/navigation";
import { serverTimestamp } from "firebase/firestore";
import posthog from "posthog-js";

export type UserType = "linkedin" | "normal" | "event";

export type AuthContextType = {
  currentUser: User | null;
  setUserDetails: (userDetails: any) => void;
  userDetails: any;
  signInWithEmail: (email: string, password: string) => Promise<UserCredential>;
  customClaims: ParsedToken | null;
  signInWithGoogle: () => Promise<void>;
  signUpWithGoogle: (role: string, userType: UserType) => Promise<void>;
  logOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userDetails, setUserDetails] = useState<any>(null);
  const [customClaims, setCustomClaims] = useState<ParsedToken | null>(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async user => {
      setCurrentUser(user ?? null);

      if (user) {
        const tokenResult = await user.getIdTokenResult();
        const token = tokenResult.token;
        const refreshToken = user.refreshToken;

        const claims = tokenResult.claims;
        setCustomClaims(claims ?? null);
        if (!userDetails) {
          const userRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserDetails(userData);
          }
        }

        if (token && refreshToken) {
          await setToken({ token: token, refreshToken: refreshToken });
        }
      } else {
        await removeToken();
      }
    });
    return () => unsubscribe();
  }, []);

  const signInWithEmail = async (email: string, password: string) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    if (result?.user) {
      const tokenResult = await result.user.getIdTokenResult();
      const token = tokenResult.token;
      const refreshToken = result.user.refreshToken;
      if (token && refreshToken) {
        await setToken({ token: token, refreshToken: refreshToken });
      }
      const userRef = doc(db, "users", result.user.uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserDetails(userData);
        posthog.identify(result.user.uid, {
          email: result.user.email,
          name: result.user.displayName
        });
        const formRef = query(
          collection(db, `${userData?.role}Profile`),
          where("uid", "==", result.user.uid)
        );
        if (userData?.accessType === "event" && userData.role === "investor") {
          router.push("/event-investor");
        } else {
          const formDoc = await getDocs(formRef);
          if (formDoc.docs.length > 0) {
            if (userData.role === "founder") {
              router.push("/home");
            } else {
              router.push("/dashboard");
            }
          } else {
            if (userData.role === "founder") {
              router.push("/founder-profile");
            } else {
              router.push("/investor-profile");
            }
          }
        }
      } else {
        router.push("/role-select");
      }
    }
  };

  const signUpWithGoogle = async (role: string, userType: UserType) => {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);

    if (result?.user) {
      const tokenResult = await result.user.getIdTokenResult();
      const token = tokenResult.token;
      const refreshToken = result.user.refreshToken;
      if (token && refreshToken) {
        await setToken({ token: token, refreshToken: refreshToken });
      }
      const userRef = doc(db, "users", result.user.uid);

      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserDetails(userData);
        posthog.identify(result.user.uid, {
          email: result.user.email,
          name: result.user.displayName
        });

        if (
          (userType === "event" || userData?.accessType === "event") &&
          userData.role === "investor"
        ) {
          router.push("/event-investor");
        } else {
          const formRef = query(
            collection(db, `${userData?.role}Profile`),
            where("uid", "==", result.user.uid)
          );
          const formDoc = await getDocs(formRef);
          if (formDoc.docs.length > 0) {
            if (userData.role === "founder") {
              router.push("/home");
            } else {
              router.push("/dashboard");
            }
          } else {
            if (userData.role === "founder") {
              router.push("/founder-profile");
            } else {
              router.push("/investor-profile");
            }
          }
        }
      } else {
        const id_value = uuidv4();

        let usedData: any = {
          id: id_value,
          uid: result.user.uid,
          email: result.user.email,
          role: role,
          userType: userType,
          eventId: "",
          activeEventId: "",
          createdDate: new Date().toISOString(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };

        posthog.identify(result.user.uid, {
          email: result.user.email,
          name: result.user.displayName
        });

        try {
          const founderEventRef = query(
            collection(db, "event1founders"),
            where("email", "==", result.user.email)
          );

          const investorEventRef = query(
            collection(db, "event1investors"),
            where("email", "==", result.user.email)
          );

          const eventDoc = await getDocs(role === "founder" ? founderEventRef : investorEventRef);

          if (eventDoc.docs.length > 0 || userType === "event") {
            usedData = {
              ...usedData,
              eventId: process.env.NEXT_PUBLIC_CURRENT_EVENT_ID,
              activeEventId: process.env.NEXT_PUBLIC_CURRENT_EVENT_ID,
              accessType: "event"
            };

            setUserDetails(usedData);
            await setDoc(userRef, usedData);

            const eventData = eventDoc.docs.map(doc => doc.data())[0];

            const founderProfileRef = doc(db, "founderProfile", id_value);
            const investorProfileRef = doc(db, "investorProfile", id_value);

            await setDoc(role === "founder" ? founderProfileRef : investorProfileRef, {
              ...eventData,
              id: id_value,
              uid: result.user.uid,
              createdDate: new Date().toISOString(),
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
          } else {
            setUserDetails(usedData);
            await setDoc(userRef, usedData);
          }

          if (role === "founder") {
            if (userType === "linkedin") {
              router.push("/linkedin-signup");
            } else {
              router.push("/founder-profile");
            }
          } else {
            if (usedData.accessType === "event") {
              router.push("/event-investor");
            } else {
              router.push("/investor-profile");
            }
          }
        } catch (error) {
          console.log("enterer sign up google errr", error);
        }
      }
    }
  };

  const logOut = async () => {
    await signOut(auth);
    setUserDetails(null);
    setCurrentUser(null);
    setCustomClaims(null);
    router.push("/");
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        signInWithEmail,
        customClaims,
        userDetails,
        setUserDetails,
        signInWithGoogle,
        signUpWithGoogle,
        logOut
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return authContext;
}
